import {Box, Typography} from "@mui/material";
import Grid from "@mui/material/Grid";
import BigNumber from "bignumber.js";
import {type ReactNode, useMemo} from "react";
import {useWatch} from "react-hook-form";
import PaymentFormPaymentAmount from "@/components/Forms/Fields/PaymentFormPaymentAmount.tsx";
import type {PaymentFormSchemaType} from "@/components/Forms/PaymentForm.tsx";
import {useCartContext} from "@/components/Providers/CartProvider.tsx";

const CashTender = (): ReactNode => {
    const {getCartTotal} = useCartContext();
    const totalCost = getCartTotal();

    const tenderedWatch = useWatch<PaymentFormSchemaType>({name: "tendered"});
    const partialPaymentAmountWatch = useWatch<PaymentFormSchemaType>({
        name: "partialPaymentAmount",
    });
    const paymentTypeWatch = useWatch<PaymentFormSchemaType>({name: "paymentType"});

    const due = useMemo(() => {
        const due =
            paymentTypeWatch === "partial"
                ? Number.parseFloat(`${partialPaymentAmountWatch}`)
                : totalCost;
        if (isNaN(due)) {
            return 0;
        }
        return due;
    }, [paymentTypeWatch, partialPaymentAmountWatch, totalCost]);

    const tendered = useMemo(() => {
        const tendered = Number.parseFloat(`${tenderedWatch}`);
        if (isNaN(tendered)) {
            return undefined;
        }
        return tendered;
    }, [tenderedWatch]);

    const change = useMemo(() => {
        if (!tendered) {
            return undefined;
        }

        if (tendered < due) {
            return undefined;
        }

        return BigNumber(tendered).minus(due).toNumber();
    }, [due, totalCost, tendered]);

    return (
        <Grid container spacing={1} sx={{width: "100%"}}>
            <Grid size={8}>
                <PaymentFormPaymentAmount
                    name="tendered"
                    label="Cash Tendered"
                    helperText="Tendered amount is required for cash payments."
                />
            </Grid>
            <Grid size={4}>
                {change !== undefined && tendered !== undefined && (
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "primary.50",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "primary.main",
                        }}
                    >
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Amount Due: ${due.toFixed(2)}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Cash Tendered: ${tendered.toFixed(2)}
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{fontWeight: "bold"}}>
                            Change Due: ${change.toFixed(2)}
                        </Typography>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default CashTender;
